import React, { useState, useEffect, useMemo } from 'react';
import './SceneWorkflow.css';
import apiService from '../../services/api';

const SceneWorkflow = ({
  projectData,
  sceneGenerationStatus,
  isGeneratingScene,
  onGenerateScene,
  onApproveScene,
  onRefineScene,
  onFinalizeProject,
  currentSceneIndex,
  setCurrentSceneIndex,
  onNewChat,
  aiModel,
}) => {
  const isSeedance = aiModel === 'seedance';
  const [refinementFeedback, setRefinementFeedback] = useState('');
  const [editedDialogue, setEditedDialogue] = useState('');
  const [showRefinementInput, setShowRefinementInput] = useState(false);
  const [showDialogueEdit, setShowDialogueEdit] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalVideo, setFinalVideo] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [isCombining, setIsCombining] = useState(false);
  const [lightboxThumb, setLightboxThumb] = useState(null);
  const [thumbnailOrder, setThumbnailOrder] = useState(null);
  const [generateThumbnails, setGenerateThumbnails] = useState(true);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [showEditScenesModal, setShowEditScenesModal] = useState(false);
  const [allScenesData, setAllScenesData] = useState(null);
  const [isLoadingScenes, setIsLoadingScenes] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [selectedSceneForVersions, setSelectedSceneForVersions] = useState(null);

  // Early return check - but after all hooks
  const hasData = projectData && projectData.scenes;
  const totalScenes = hasData ? projectData.scenes.length : 0;
  const currentScene = hasData ? projectData.scenes[currentSceneIndex] : null;

  // Wrap sceneStatus in useMemo to avoid dependency issues
  const sceneStatus = useMemo(() => {
    return currentScene ? (sceneGenerationStatus[currentScene.sceneNumber] || {}) : {};
  }, [currentScene, sceneGenerationStatus]);

  // Check if all scenes are approved
  const allScenesApproved = hasData ? projectData.scenes.every(scene =>
    sceneGenerationStatus[scene.sceneNumber]?.status === 'approved'
  ) : false;

  // Check if this is a single scene project
  const isSingleScene = hasData && projectData.scenes.length === 1;

  // Handle retry countdown timer
  useEffect(() => {
    if (sceneStatus.status === 'failed' && sceneStatus.retryAfter && sceneStatus.retryAfter > 0) {
      setRetryCountdown(sceneStatus.retryAfter);

      const timer = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setRetryCountdown(0);
    }
  }, [sceneStatus.status, sceneStatus.retryAfter]);

  // Get statistics from backend or calculate as fallback
  const projectStats = useMemo(() => {
    if (!hasData) return {
      totalVideosGenerated: 0,
      totalRefinements: 0,
      totalGenerationTimeSec: 0,
      totalCost: 0,
      currency: 'USD',
      scenesApproved: 0
    };

    // Use backend statistics if available
    if (projectData.statistics) {
      const stats = projectData.statistics;
      let scenesApproved = 0;
      projectData.scenes.forEach(scene => {
        if (sceneGenerationStatus[scene.sceneNumber]?.status === 'approved') {
          scenesApproved++;
        }
      });

      return {
        totalVideosGenerated: stats.totalVideosGenerated || 0,
        totalRefinements: stats.totalRefinements || 0,
        totalVideoDurationSec: stats.totalVideoDurationSec || 0,
        totalGenerationTimeSec: stats.totalGenerationTimeSec || 0,
        totalCost: stats.billing?.totalCost || 0,
        currency: stats.billing?.currency || 'USD',
        scenesApproved
      };
    }

    // Fallback: calculate from scene data
    let scenesApproved = 0;
    projectData.scenes.forEach(scene => {
      if (sceneGenerationStatus[scene.sceneNumber]?.status === 'approved') {
        scenesApproved++;
      }
    });

    return {
      totalVideosGenerated: 0,
      totalRefinements: 0,
      totalVideoDurationSec: 0,
      totalGenerationTimeSec: 0,
      totalCost: 0,
      currency: 'USD',
      scenesApproved
    };
  }, [hasData, projectData, sceneGenerationStatus]);

  // Auto-generate scene when it's pending (not generated yet)
  useEffect(() => {
    if (hasData && currentScene && !sceneStatus.videoUrl &&
      (!sceneStatus.status || sceneStatus.status === 'pending') &&
      !isGeneratingScene) {
      // Automatically start generating the current scene if it hasn't been generated yet
      const timer = setTimeout(async () => {
        try {
          await onGenerateScene(projectData.projectId, currentScene.sceneNumber);
        } catch (error) {
          // Error is already handled in generateScene, but catch here to prevent crashes
          console.error('Error generating scene:', error);
        }
      }, 500); // Small delay to ensure UI is ready
      return () => clearTimeout(timer);
    }
  }, [hasData, currentScene, sceneStatus, isGeneratingScene, projectData, onGenerateScene]);

  // Auto-advance to next scene when current is approved
  useEffect(() => {
    if (hasData && sceneStatus.status === 'approved' && currentSceneIndex < totalScenes - 1) {
      const timer = setTimeout(() => {
        setCurrentSceneIndex(prev => prev + 1);
        setShowRefinementInput(false);
        setRefinementFeedback('');
        setEditedDialogue('');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [sceneStatus.status, currentSceneIndex, totalScenes, hasData, setCurrentSceneIndex]);

  // Reset dialogue editor when scene changes
  useEffect(() => {
    if (hasData && currentScene) {
      setEditedDialogue(currentScene.dialogue || '');
    }
  }, [hasData, currentScene]);

  // Early return AFTER all hooks
  if (!hasData) {
    return null;
  }

  const handleApproveScene = async () => {
    const getLatestRefinementIndex = () => {
      if (sceneStatus?.refinements && sceneStatus.refinements.length > 0) {
        return sceneStatus.refinements.length - 1;
      }
      if (typeof sceneStatus?.refinementsCount === 'number' && sceneStatus.refinementsCount > 0) {
        return sceneStatus.refinementsCount - 1;
      }
      return 0;
    };

    const latestIndex = getLatestRefinementIndex();
    try {
      await onApproveScene(projectData.projectId, currentScene.sceneNumber, latestIndex);
    } catch (error) {
      console.error('Failed to approve scene:', error);
    }
  };

  const handleApprovePreviousVersion = async (refinementIndex) => {
    try {
      // Call the approve endpoint with the specific refinement index
      // The backend should use the videoUrl from that specific refinement
      await onApproveScene(projectData.projectId, currentScene.sceneNumber, refinementIndex);
    } catch (error) {
      console.error('Failed to approve previous version:', error);
    }
  };


  const handleRefineScene = async () => {
    // Must have at least one of the fields filled
    if (!refinementFeedback.trim() && !showDialogueEdit) return;

    // Check if the original scene has empty dialogue (dialogue: false case)
    const originalDialogue = currentScene.dialogue || '';
    const isEmptyDialogueScene = originalDialogue === '';

    // Determine what dialogue value to send
    let dialogueToSend = undefined;  // undefined means don't include in API call

    if (isEmptyDialogueScene) {
      // Always send empty string for scenes created with dialogue: false
      dialogueToSend = '';
    } else {
      // For scenes with dialogue, only send if user edited it
      const dialogueChanged = showDialogueEdit && editedDialogue.trim() !== originalDialogue.trim();
      if (dialogueChanged) {
        dialogueToSend = editedDialogue.trim();
      }
      // If not changed, leave as undefined (won't be included in API call)
    }

    // Capture values before clearing state
    const feedbackToSend = refinementFeedback.trim();
    const dialogueValue = dialogueToSend;

    // Close refinement section immediately after submission
    setShowRefinementInput(false);
    setShowDialogueEdit(false);
    setRefinementFeedback('');
    setEditedDialogue('');

    try {
      await onRefineScene(
        projectData.projectId,
        currentScene.sceneNumber,
        feedbackToSend,
        dialogueValue  // Will be "" for empty dialogue scenes, undefined for unchanged dialogue
      );
    } catch (error) {
      console.error('Failed to refine scene:', error);
      // Re-open refinement section on error so user can retry
      setShowRefinementInput(true);
      setRefinementFeedback(feedbackToSend);
      if (!isEmptyDialogueScene && dialogueValue !== undefined) {
        setEditedDialogue(dialogueValue);
        setShowDialogueEdit(true);
      }
    }
  };

const handleFinalizeProject = async () => {
  setIsFinalizing(true);

  try {
    const result = await onFinalizeProject(projectData.projectId, generateThumbnails);
    setFinalVideo(result);

    const projectId = result?.projectId || projectData?.projectId;
    const finalVideoUrl = result?.finalVideoUrl || result?.videoUrl;

    // Prefer a real topic/prompt from the project data if available
    const topic =
      projectData?.prompt ||
      projectData?.topic ||
      projectData?.scenes?.[0]?.dialogue ||
      undefined;

    if (projectId && finalVideoUrl) {
      window.parent.postMessage(
        {
          type: "VIDEO_TASK_COMPLETE",
          contentId: projectId,
          topic,
        },
        "*"
      );
    }
  } catch (error) {
    console.error("Failed to finalize project:", error);
  } finally {
    setIsFinalizing(false);
  }
};

  const handleEditScenes = async () => {
    // Redirect to editor with projectId in new tab
    if (projectData?.projectId) {
      const editorUrl = `https://video.speedstack.tech/?importProjectId=${projectData.projectId}`;
      window.open(editorUrl, '_blank');
    }
  };

  const handleEditScenesModal = async () => {
    setShowEditScenesModal(true);
    setIsLoadingScenes(true);
    try {
      // Fetch all scenes data using GET API
      const fetchedProjectData = await apiService.getProject(projectData?.projectId);
      setAllScenesData(fetchedProjectData);
    } catch (error) {
      console.error('Failed to fetch scenes:', error);
    } finally {
      setIsLoadingScenes(false);
    }
  };

  const downloadScene = (videoUrl, sceneNumber, label = '') => {
    const link = document.createElement('a');
    link.href = videoUrl;
    const fileName = label
      ? `scene-${sceneNumber}-${label.toLowerCase().replace(/\s+/g, '-')}.mp4`
      : `scene-${sceneNumber}.mp4`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // If final video is ready, show it
  if (finalVideo) {
    return (
      <div className="scene-workflow-container">
        {/* Project Statistics - Final Summary */}
        <div className="project-stats-bar">
          <div className="stat-item highlight">
            <span className="stat-icon">🎥</span>
            <div className="stat-info">
              <span className="stat-label">Videos Generated</span>
              <span className="stat-value">{projectStats.totalVideosGenerated}</span>
            </div>
          </div>
          <div className="stat-item">
            <span className="stat-icon">✨</span>
            <div className="stat-info">
              <span className="stat-label">Refinements</span>
              <span className="stat-value">{projectStats.totalRefinements}</span>
            </div>
          </div>
          <div className="stat-item cost">
            <span className="stat-icon">💰</span>
            <div className="stat-info">
              <span className="stat-label">Total Cost</span>
              <span className="stat-value">${projectStats.totalCost} {projectStats.currency}</span>
            </div>
          </div>
        </div>

        <div className="final-video-section">
          <div className="success-header">
            <h3>Video Ready!</h3>
          </div>

          <div className="final-video-player">
            <video controls style={{ width: '100%', maxWidth: '500px', borderRadius: '6px' }}>
              <source src={finalVideo.finalVideoUrl || finalVideo.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          {finalVideo.thumbnails && finalVideo.thumbnails.length > 0 && (
            <div style={{ width: '100%', marginTop: '20px' }}>
              <h4 style={{ marginBottom: '4px', textAlign: 'center', fontSize: '15px', fontWeight: '600' }}>🖼️ Select a Thumbnail Intro</h4>
              <p style={{ textAlign: 'center', fontSize: '12px', color: '#666', marginBottom: '12px' }}>Click image to preview • ❤️ to set as main thumbnail</p>
              {(() => {
                const thumbs = thumbnailOrder || finalVideo.thumbnails;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                    {thumbs.map((thumb, i) => (
                      <div
                        key={thumb.url}
                        style={{
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: selectedThumbnail?.url === thumb.url ? '3px solid #4a90e2' : i === 0 ? '2px solid #f59e0b' : '2px solid #ddd',
                          background: '#f9f9f9',
                          transition: 'all 0.15s ease',
                          boxShadow: selectedThumbnail?.url === thumb.url ? '0 0 0 2px #4a90e2' : 'none',
                          position: 'relative'
                        }}
                      >
                        {i === 0 && (
                          <div style={{ background: '#f59e0b', color: '#fff', fontSize: '10px', fontWeight: '700', textAlign: 'center', padding: '2px' }}>⭐ Main</div>
                        )}
                        {selectedThumbnail?.url === thumb.url && i !== 0 && (
                          <div style={{ background: '#4a90e2', color: '#fff', fontSize: '11px', fontWeight: '700', textAlign: 'center', padding: '3px' }}>✓ Selected</div>
                        )}
                        <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightboxThumb(thumb)}>
                          <img src={thumb.url} alt={`Thumbnail ${i + 1}`} style={{ width: '100%', display: 'block' }} />
                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                            onMouseLeave={e => e.currentTarget.style.opacity = 0}
                          >
                            <span style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🔍</span>
                          </div>
                        </div>
                        <div style={{ padding: '8px', fontSize: '11px', color: '#555' }}>
                          <div style={{ fontStyle: 'italic', marginBottom: '4px' }}>"{thumb.hookText}"</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Score: {(thumb.score * 100).toFixed(0)}%</span>
                            <button
                              title={i === 0 ? 'Already main thumbnail' : 'Set as main thumbnail'}
                              onClick={() => {
                                if (i === 0) return;
                                const reordered = [thumb, ...thumbs.filter((_, idx) => idx !== i)];
                                setThumbnailOrder(reordered);
                                setSelectedThumbnail(thumb);
                                apiService.reorderThumbnails(
                                  finalVideo.projectId || projectData?.projectId,
                                  thumb.url
                                ).catch(err => console.error('Failed to reorder thumbnails:', err));
                              }}
                              style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', fontSize: '16px', padding: '0', lineHeight: 1 }}
                            >
                              {i === 0 ? '❤️' : '🤍'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Lightbox */}
              {lightboxThumb && (
                <div
                  onClick={() => setLightboxThumb(null)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                >
                  <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                    <img src={lightboxThumb.url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '8px', display: 'block' }} />
                    <button
                      onClick={() => setLightboxThumb(null)}
                      style={{ position: 'absolute', top: '-12px', right: '-12px', background: '#fff', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px', fontWeight: '700', lineHeight: 1 }}
                    >✕</button>
                    <div style={{ marginTop: '8px', textAlign: 'center', color: '#fff', fontSize: '13px', fontStyle: 'italic' }}>
                      "{lightboxThumb.hookText}"
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

  {finalVideo.languageVideos && finalVideo.languageVideos.length > 0 && (
  <div style={{ width: '100%', marginTop: '24px' }}>
    <h4 style={{ marginBottom: '12px', textAlign: 'center' }}>Language Versions</h4>
    <div style={{
      display: 'grid',
      gridTemplateColumns: finalVideo.languageVideos.flat().length === 1 
        ? '1fr'           // 1 version → full width
        : 'repeat(2, 1fr)', // 2+ versions → 2 columns
      gap: '16px',
      width: '100%'
    }}>
      {finalVideo.languageVideos.flat().map((langObj, index) => {
        const [langName, langUrl] = Object.entries(langObj)[0];
        return (
          <div key={index} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '500px',   // ← add this
  margin: '0 auto',    // ← center it
  marginBottom: '16px'
          }}>
            <p style={{ fontWeight: '600', marginBottom: '8px' }}>{langName}</p>
            <video controls style={{ width: '100%', borderRadius: '6px' }}>
              <source src={langUrl} type="video/mp4" />
            </video>
          </div>
        );
      })}
    </div>
  </div>
)}

          <div className="final-video-actions">
            <div className="final-video-link">
              <a
                href={`https://marketops.speedstack.tech/contentupload?clientId=${new URLSearchParams(window.location.search).get('clientId') || 'default'}&autoShare=true`}
                target="_top"
                className="download-link"
              >
                Share Video with social media
              </a>
            </div>

            <div className="final-video-link">
              <button
                onClick={handleEditScenes}
                className="download-link"
                style={{ border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #4a90e2, #357abd)' }}
              >
                add effects to the video
              </button>
            </div>

            <div className="final-video-link">
              <button
                onClick={onNewChat}
                className="download-link"
                style={{ border: 'none', cursor: 'pointer', background: '#6b7280', color: '#fff' }}
              >
                Create New Project
              </button>
            </div>
          </div>
        </div>

        {/* Edit Scenes Modal */}
        {showEditScenesModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: '20px'
          }} onClick={() => setShowEditScenesModal(false)}>
            <div className="modal-content" style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '1000px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#000' }}>
                  Add Effects to the Video
                </h3>
                <button
                  onClick={() => setShowEditScenesModal(false)}
                  style={{
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#666',
                    fontSize: '20px',
                    cursor: 'pointer',
                    lineHeight: '1'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{
                background: '#f0f7ff',
                border: '1px solid #4a90e2',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
                  <strong>📥 Step 1:</strong> Download all scenes from below. Each scene can be downloaded individually.<br />
                  <strong>✏️ Step 2:</strong> After downloading, click the link below to open SpeedStack Video Editor.<br />
                  <strong>🎬 Step 3:</strong> Upload your downloaded scenes in the editor and make your edits (speed adjustments, effects, etc.).
                </p>
              </div>

              {isLoadingScenes ? (
                <div className="flex items-center gap-3 p-4">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-700">Loading scenes...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {allScenesData?.scenes?.map((scene) => {
                    const refinements = scene.refinements || [];
                    const hasVersions = refinements.length > 0;

                    return (
                      <div key={scene.sceneNumber} style={{
                        display: 'flex',
                        gap: '24px',
                        padding: '20px',
                        background: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #ddd',
                        alignItems: 'stretch'
                      }}>
                        {/* Left side - Approved video */}
                        <div style={{ flex: '0 0 400px', maxWidth: '400px', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#000' }}>
                              Scene {scene.sceneNumber}
                            </h5>
                            <span style={{ fontSize: '12px', color: '#4a90e2', fontWeight: '600' }}>
                              ✓ Approved
                            </span>
                          </div>
                          {scene.videoUrl ? (
                            <>
                              <video
                                controls
                                style={{
                                  width: '100%',
                                  maxWidth: '400px',
                                  height: 'auto',
                                  borderRadius: '8px',
                                  marginBottom: '12px',
                                  display: 'block'
                                }}
                              >
                                <source src={scene.videoUrl} type="video/mp4" />
                              </video>
                              <button
                                onClick={() => downloadScene(scene.videoUrl, scene.sceneNumber, 'Approved')}
                                className="btn btn-primary"
                                style={{ width: '100%', fontSize: '13px', padding: '10px', maxWidth: '400px' }}
                              >
                                Download Approved
                              </button>
                            </>
                          ) : (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px', background: '#f5f5f5', borderRadius: '8px' }}>
                              Scene not generated yet
                            </div>
                          )}
                        </div>

                        {/* Right side - View All button */}
                        <div style={{
                          flex: '1 1 auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '100%'
                        }}>
                          {hasVersions ? (
                            <button
                              onClick={() => setSelectedSceneForVersions(scene)}
                              className="btn btn-primary"
                              style={{
                                padding: '12px 24px',
                                fontSize: '14px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                minWidth: '180px'
                              }}
                            >
                              View All Scene {scene.sceneNumber} {refinements.length === 1 ? 'Video' : 'Videos'}
                            </button>
                          ) : (
                            <div style={{
                              padding: '20px',
                              textAlign: 'center',
                              color: '#999',
                              fontSize: '13px',
                              minWidth: '180px'
                            }}>
                              No other versions
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
                <a
                  href="https://video.speedstack.tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                  style={{
                    display: 'inline-block',
                    padding: '12px 32px',
                    fontSize: '15px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #4a90e2, #357abd)',
                    color: '#fff',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Open Video Editor
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Popup for viewing all versions of a scene */}
        {selectedSceneForVersions && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: '20px'
          }} onClick={() => setSelectedSceneForVersions(null)}>
            <div className="modal-content" style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '1000px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={() => setSelectedSceneForVersions(null)}
                    style={{
                      padding: '8px 12px',
                      background: '#f5f5f5',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      color: '#333',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ← Back
                  </button>
                  <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#000' }}>
                    Scene {selectedSceneForVersions.sceneNumber} - Refinement Versions
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedSceneForVersions(null)}
                  style={{
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#666',
                    fontSize: '20px',
                    cursor: 'pointer',
                    lineHeight: '1'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Only show refinement versions (not approved) */}
              {selectedSceneForVersions.refinements && selectedSceneForVersions.refinements.length > 0 ? (
                <div className="scenes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  {/* All refinement versions in reverse order */}
                  {[...selectedSceneForVersions.refinements].reverse().map((refinement, reverseIndex) => {
                    const originalIndex = selectedSceneForVersions.refinements.length - 1 - reverseIndex;
                    return (
                      <div key={originalIndex} style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '16px',
                        background: '#fff'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#666',
                            textTransform: 'uppercase'
                          }}>
                            Version {originalIndex + 1}
                          </span>
                          {refinement.instructions && (
                            <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic', maxWidth: '60%', textAlign: 'right' }}>
                              {refinement.instructions}
                            </span>
                          )}
                        </div>
                        <video
                          controls
                          style={{ width: '100%', borderRadius: '6px', marginBottom: '12px' }}
                        >
                          <source src={refinement.videoUrl} type="video/mp4" />
                        </video>
                        <button
                          onClick={() => downloadScene(refinement.videoUrl, selectedSceneForVersions.sceneNumber, `Version ${originalIndex + 1}`)}
                          className="btn btn-primary"
                          style={{ width: '100%', fontSize: '12px', padding: '10px' }}
                        >
                          Download Version {originalIndex + 1}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px', background: '#f5f5f5', borderRadius: '8px' }}>
                  No refinement versions available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="scene-workflow-container">
      {/* Project Statistics */}
      <div className="project-stats-bar">
        <div className="stat-item highlight">
          <span className="stat-icon">🎥</span>
          <div className="stat-info">
            <span className="stat-label">Videos Generated</span>
            <span className="stat-value">{projectStats.totalVideosGenerated}</span>
          </div>
        </div>
        <div className="stat-item">
          <span className="stat-icon">✨</span>
          <div className="stat-info">
            <span className="stat-label">Refinements</span>
            <span className="stat-value">{projectStats.totalRefinements}</span>
          </div>
        </div>
        <div className="stat-item cost">
          <span className="stat-icon">💰</span>
          <div className="stat-info">
            <span className="stat-label">Total Cost</span>
            <span className="stat-value">${projectStats.totalCost} {projectStats.currency}</span>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="workflow-progress">
        <div className="progress-header">
          <h3>Scene-by-Scene Video Generation</h3>
          <span className="scene-counter">
            Scene {currentSceneIndex + 1} of {totalScenes}
          </span>
        </div>

        <div className="progress-bar-wrapper">
          <div className="progress-bar-bg">
            <div
              className="progress-bar-fill"
              style={{ width: `${((currentSceneIndex + (sceneStatus.status === 'approved' ? 1 : 0)) / totalScenes) * 100}%` }}
            />
          </div>
          <div className="progress-indicators">
            {projectData.scenes.map((scene, index) => {
              const status = sceneGenerationStatus[scene.sceneNumber]?.status;
              return (
                <div
                  key={scene.sceneNumber}
                  className={`progress-dot ${index < currentSceneIndex ? 'completed' :
                    index === currentSceneIndex ? 'active' : 'pending'
                    } ${status === 'approved' ? 'approved' :
                      status === 'failed' ? 'failed' : ''
                    }`}
                >
                  {status === 'approved' ? '✓' :
                    status === 'failed' ? '⚠' : scene.sceneNumber}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Scene Only */}
      <div className="current-scene-section">
        <div className="scene-header">
          <h4>Scene {currentScene.sceneNumber}</h4>
        </div>


        {/* Video Preview - Show Current and Previous for Comparison */}
        {sceneStatus.videoUrl && (
          <div className="scene-video-section">
            <label>Scene {currentScene.sceneNumber} Preview:</label>

            <div className="video-comparison-grid">
              {/* Current/Latest Video */}
              <div className="video-card current-video">
                <div className="video-card-header">
                  <span className="video-label">Current Version</span>
                  {sceneStatus.refinementsCount > 0 && (
                    <span className="refinement-badge">
                      Version {sceneStatus.refinementsCount}
                    </span>
                  )}
                </div>
                <div className="video-player-wrapper">
                  <video
                    controls
                    key={sceneStatus.videoUrl}
                    className="scene-video"
                  >
                    <source src={sceneStatus.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="video-footer">
                  {sceneStatus.generationTime && (
                    <div className="video-meta">
                      ⏱ {(sceneStatus.generationTime / 1000).toFixed(1)}s
                    </div>
                  )}
                  {sceneStatus.status !== 'approved' && !isGeneratingScene && (
                    <button
                      className="btn-approve-video"
                      onClick={handleApproveScene}
                    >
                      ✓ Approve This
                    </button>
                  )}
                </div>
              </div>

              {/* Previous Video (if there was a refinement) */}
              {sceneStatus.refinements && sceneStatus.refinements.length > 1 && (
                <div className="video-card previous-video">
                  <div className="video-card-header">
                    <span className="video-label">Previous Version</span>
                    <span className="refinement-badge secondary">
                      Version {sceneStatus.refinementsCount - 1}
                    </span>
                  </div>
                  <div className="video-player-wrapper">
                    <video
                      controls
                      key={sceneStatus.refinements[sceneStatus.refinements.length - 2].videoUrl}
                      className="scene-video"
                    >
                      <source
                        src={sceneStatus.refinements[sceneStatus.refinements.length - 2].videoUrl}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="video-footer">
                    <div className="video-meta">
                      {sceneStatus.refinements[sceneStatus.refinements.length - 2].instructions}
                    </div>
                    {sceneStatus.status !== 'approved' && !isGeneratingScene && (
                      <button
                        className="btn-approve-video"
                        onClick={() => handleApprovePreviousVersion(sceneStatus.refinements.length - 2)}
                      >
                        ✓ Approve This
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Toggle to show all versions if more than 2 exist */}
            {sceneStatus.refinements && sceneStatus.refinements.length > 2 && (
              <div className="refinement-history-toggle">
                <button
                  className="btn-toggle-history"
                  onClick={() => setShowAllVersions(!showAllVersions)}
                >
                  {showAllVersions ? '▲ Hide' : '▼ View'} All {sceneStatus.refinements.length} Versions
                </button>

                {showAllVersions && (
                  <div className="all-versions-grid">
                    {[...sceneStatus.refinements].reverse().map((refinement, reverseIndex) => {
                      const originalIndex = sceneStatus.refinements.length - 1 - reverseIndex;
                      const isLatest = originalIndex === sceneStatus.refinements.length - 1;
                      return (
                        <div key={originalIndex} className="video-card">
                          <div className="video-card-header">
                            <span className="video-label">
                              {isLatest ? 'Current' : `Version ${originalIndex + 1}`}
                            </span>
                            <span className={`refinement-badge ${isLatest ? '' : 'secondary'}`}>
                              V{originalIndex + 1}
                            </span>
                          </div>
                          <div className="video-player-wrapper">
                            <video
                              controls
                              key={refinement.videoUrl}
                              className="scene-video"
                            >
                              <source src={refinement.videoUrl} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                          <div className="video-footer">
                            {refinement.instructions && (
                              <div className="video-meta">
                                📝 {refinement.instructions}
                              </div>
                            )}
                            {sceneStatus.status !== 'approved' && !isGeneratingScene && (
                              <button
                                className="btn-approve-video"
                                onClick={() => isLatest ? handleApproveScene() : handleApprovePreviousVersion(originalIndex)}
                              >
                                ✓ Approve This
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="scene-actions">
          {!sceneStatus.videoUrl && !sceneStatus.status && !isGeneratingScene && (
            <div className="auto-start-indicator">
              <div className="loading-dots">
                <span>●</span><span>●</span><span>●</span>
              </div>
              <span>Starting generation for Scene {currentScene.sceneNumber}...</span>
            </div>
          )}

          {isGeneratingScene && (
            <div className="generating-indicator flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-gray-700">Generating video... This may take 60-90 seconds</span>
            </div>
          )}

          {sceneStatus.status === 'failed' && !isGeneratingScene && (
            <div className="error-indicator">
              <div className="error-icon">⚠️</div>
              <div className="error-content" style={{ width: '100%' }}>
                {sceneStatus.errorMessage && (
                  <p className="error-message">{sceneStatus.errorMessage}</p>
                )}

                <div style={{ marginTop: '16px', marginBottom: '16px', textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                    Edit Dialogue Before Retry:
                  </label>
                  <textarea
                    value={editedDialogue}
                    onChange={(e) => setEditedDialogue(e.target.value)}
                    placeholder="Edit the dialogue text here before retrying..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      color: '#333',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    {editedDialogue?.split(/\s+/).filter(w => w.length > 0).length || 0} words
                  </small>
                </div>

                {retryCountdown > 0 && (
                  <p className="retry-info" style={{ fontSize: '13px', color: '#7f1d1d', marginTop: '8px', marginBottom: '16px', fontWeight: '500' }}>
                    Please wait {retryCountdown} second{retryCountdown !== 1 ? 's' : ''} before retrying.
                  </p>
                )}

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-retry"
                    disabled={retryCountdown > 0}
                    onClick={async () => {
                      try {
                        const dialogueToSend = editedDialogue.trim() !== (currentScene.dialogue || '').trim() ? editedDialogue.trim() : undefined;
                        await onGenerateScene(projectData.projectId, currentScene.sceneNumber, {
                          ...(dialogueToSend !== undefined && { dialogue: dialogueToSend })
                        });
                      } catch (error) {
                        // Error is already handled in generateScene, but catch here to prevent crashes
                        console.error('Error retrying scene generation:', error);
                      }
                    }}
                  >
                    🔄 Retry Generation
                  </button>

                  {onNewChat && (
                    <button
                      className="btn btn-secondary"
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        background: '#f5f5f5',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                      onClick={() => onNewChat()}
                    >
                      Create New Project
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {sceneStatus.videoUrl && sceneStatus.status !== 'approved' && !isGeneratingScene && !isSeedance && (
            <div className="refine-action-center">
              <button
                className="btn btn-refine"
                onClick={() => setShowRefinementInput(!showRefinementInput)}
              >
                Refine Video
              </button>
            </div>
          )}

          {sceneStatus.videoUrl && sceneStatus.status !== 'approved' && !isGeneratingScene && isSeedance && (
            <div className="refine-action-center">
              <button
                className="btn btn-approve-video"
                onClick={handleApproveScene}
              >
                ✓ Approve Scene
              </button>
            </div>
          )}

          {allScenesApproved && !isFinalizing && (
            <div className="refine-action-center" style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={generateThumbnails}
                  onChange={(e) => setGenerateThumbnails(e.target.checked)}
                  style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                />
                Generate Thumbnails
              </label>
              <button
                className="btn btn-refine"
                onClick={handleFinalizeProject}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Generate Final Video
              </button>
              <button
                className="btn btn-refine"
                onClick={handleEditScenes}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                add effects to the video
              </button>
              <button
                className="btn btn-refine"
                onClick={handleEditScenesModal}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none'
                }}
              >
                View All Generated Videos
              </button>
            </div>
          )}

          {isFinalizing && (
            <div className="generating-indicator flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-gray-700">
                {isSingleScene
                  ? "Creating your final video... Please wait."
                  : "Combining all scenes into final video... Please wait."
                }
              </span>
            </div>
          )}

          {showEditScenesModal && (
            <div className="modal-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              padding: '20px'
            }} onClick={() => setShowEditScenesModal(false)}>
              <div className="modal-content" style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#000' }}>
                    View All Generated Videos
                  </h3>
                  <button
                    onClick={() => setShowEditScenesModal(false)}
                    style={{
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#666',
                      fontSize: '20px',
                      cursor: 'pointer',
                      lineHeight: '1'
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{
                  background: '#f0f7ff',
                  border: '1px solid #4a90e2',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
                    View all generated scenes below. Each scene can be downloaded individually.
                  </p>
                </div>

                {isLoadingScenes ? (
                  <div className="flex items-center gap-3 p-4">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-gray-700">Loading scenes...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {allScenesData?.scenes?.map((scene) => {
                      const refinements = scene.refinements || [];
                      const allVersions = [];

                      // Collect all versions (refinements + approved)
                      refinements.forEach((refinement, index) => {
                        if (refinement.videoUrl) {
                          allVersions.push({
                            videoUrl: refinement.videoUrl,
                            versionNumber: index + 1,
                            isApproved: false
                          });
                        }
                      });

                      // Add approved video if it exists and is unique
                      if (scene.videoUrl) {
                        const isUnique = !allVersions.some(v => v.videoUrl === scene.videoUrl);
                        if (isUnique) {
                          allVersions.push({
                            videoUrl: scene.videoUrl,
                            versionNumber: refinements.length + 1,
                            isApproved: true
                          });
                        } else {
                          // Mark the matching version as approved
                          const matchingVersion = allVersions.find(v => v.videoUrl === scene.videoUrl);
                          if (matchingVersion) {
                            matchingVersion.isApproved = true;
                          }
                        }
                      }

                      return (
                        <div key={scene.sceneNumber} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '16px',
                          padding: '20px',
                          background: '#fff',
                          borderRadius: '12px',
                          border: '1px solid #ddd'
                        }}>
                          {/* Scene header */}
                          <div style={{ marginBottom: '12px' }}>
                            <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#000' }}>
                              Scene {scene.sceneNumber}
                            </h5>
                          </div>

                          {/* All versions grid */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {allVersions.map((version, index) => (
                              <div key={index} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                background: '#f9f9f9',
                                borderRadius: '8px',
                                padding: '12px',
                                border: version.isApproved ? '2px solid #4a90e2' : '1px solid #ddd'
                              }}>
                                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#000' }}>
                                    Version {version.versionNumber}
                                  </span>
                                  {version.isApproved && (
                                    <span style={{ fontSize: '12px', color: '#4a90e2', fontWeight: '600' }}>
                                      ✓ Approved
                                    </span>
                                  )}
                                </div>
                                {version.videoUrl ? (
                                  <>
                                    <video
                                      controls
                                      style={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: '6px',
                                        marginBottom: '8px',
                                        display: 'block'
                                      }}
                                    >
                                      <source src={version.videoUrl} type="video/mp4" />
                                    </video>
                                    <button
                                      onClick={() => downloadScene(version.videoUrl, scene.sceneNumber, `v${version.versionNumber}`)}
                                      className="btn btn-primary"
                                      style={{ width: '100%', fontSize: '12px', padding: '8px' }}
                                    >
                                      Download v{version.versionNumber}
                                    </button>
                                  </>
                                ) : (
                                  <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                                    Video not available
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Popup for viewing all versions of a scene */}
          {selectedSceneForVersions && (
            <div className="modal-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2000,
              padding: '20px'
            }} onClick={() => setSelectedSceneForVersions(null)}>
              <div className="modal-content" style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '1000px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                      onClick={() => setSelectedSceneForVersions(null)}
                      style={{
                        padding: '8px 12px',
                        background: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        color: '#333',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      ← Back
                    </button>
                    <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#000' }}>
                      Scene {selectedSceneForVersions.sceneNumber} - Refinement Versions
                    </h3>
                  </div>
                  <button
                    onClick={() => setSelectedSceneForVersions(null)}
                    style={{
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#666',
                      fontSize: '20px',
                      cursor: 'pointer',
                      lineHeight: '1'
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Only show refinement versions (not approved) */}
                {selectedSceneForVersions.refinements && selectedSceneForVersions.refinements.length > 0 ? (
                  <div className="scenes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    {/* All refinement versions in reverse order */}
                    {[...selectedSceneForVersions.refinements].reverse().map((refinement, reverseIndex) => {
                      const originalIndex = selectedSceneForVersions.refinements.length - 1 - reverseIndex;
                      return (
                        <div key={originalIndex} style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '16px',
                          background: '#fff'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px'
                          }}>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#666',
                              textTransform: 'uppercase'
                            }}>
                              Version {originalIndex + 1}
                            </span>
                            {refinement.instructions && (
                              <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic', maxWidth: '60%', textAlign: 'right' }}>
                                {refinement.instructions}
                              </span>
                            )}
                          </div>
                          <video
                            controls
                            style={{ width: '100%', borderRadius: '6px', marginBottom: '12px' }}
                          >
                            <source src={refinement.videoUrl} type="video/mp4" />
                          </video>
                          <button
                            onClick={() => downloadScene(refinement.videoUrl, selectedSceneForVersions.sceneNumber, `Version ${originalIndex + 1}`)}
                            className="btn btn-primary"
                            style={{ width: '100%', fontSize: '12px', padding: '10px' }}
                          >
                            Download Version {originalIndex + 1}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px', background: '#f5f5f5', borderRadius: '8px' }}>
                    No refinement versions available
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Refinement Input */}
        {showRefinementInput && (
          <div className="refinement-section">
            <div style={{ marginBottom: '20px' }}>
              <label>Visual Refinement Feedback:</label>
              <textarea
                value={refinementFeedback}
                onChange={(e) => setRefinementFeedback(e.target.value)}
                placeholder="Describe what you'd like to change visually (e.g., 'make all people Indian origin', 'change background to beach', etc.)"
                rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ color: showDialogueEdit ? '#000' : '#999' }}>Dialogue:</label>
                {!showDialogueEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowDialogueEdit(true);
                      setEditedDialogue(currentScene.dialogue || '');
                    }}
                    style={{
                      padding: '4px 12px',
                      background: 'transparent',
                      border: '1px solid #4a90e2',
                      borderRadius: '4px',
                      color: '#4a90e2',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              {showDialogueEdit ? (
                <>
                  <textarea
                    value={editedDialogue}
                    onChange={(e) => setEditedDialogue(e.target.value)}
                    placeholder="Edit the dialogue text here..."
                    rows={2}
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'inherit' }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    {editedDialogue?.split(/\s+/).filter(w => w.length > 0).length || 0} words
                  </small>
                </>
              ) : (
                <div style={{
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  background: '#f5f5f5',
                  color: '#999',
                  fontStyle: 'italic',
                  fontSize: '14px'
                }}>
                  {currentScene.dialogue || 'No dialogue'}
                </div>
              )}
            </div>

            <div className="refinement-actions">
              <button
                className="btn btn-primary"
                onClick={handleRefineScene}
                disabled={(!refinementFeedback.trim() && !showDialogueEdit) || isGeneratingScene}
              >
                Apply Refinement
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRefinementInput(false);
                  setRefinementFeedback('');
                  setEditedDialogue(currentScene.dialogue || '');
                  setShowDialogueEdit(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SceneWorkflow;
